import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { LogIn } from 'lucide-react';
import { clsx } from 'clsx';
import Drawer from '../../components/ui/Drawer';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { platformAdminApi, type AdminUser } from '../../services/platform-admin-api';
import StatusPill from './StatusPill';
import { formatDate } from './format';

interface Props {
  userId: string | null;
  onClose: () => void;
  onImpersonate: (user: AdminUser) => void;
}

const AdminUserDrawer: React.FC<Props> = ({ userId, onClose, onImpersonate }) => {
  const { data: user, isLoading } = useQuery({
    queryKey: ['platformAdminUser', userId],
    queryFn: () => platformAdminApi.getUser(userId!),
    enabled: !!userId,
  });

  const name = user ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() : '';
  const canImpersonate = !!user && user.status === 'active' && !user.platformRole;

  return (
    <Drawer isOpen={!!userId} onClose={onClose} title={name || user?.email || 'User'} width="lg">
      {isLoading || !user ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-6 px-5 pb-8">
          <section>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={user.status} />
              {user.platformRole && (
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-primary">
                  Platform {user.platformRole}
                </span>
              )}
              {user.isInternalTester && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                  Internal tester
                </span>
              )}
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <Field label="Email" value={user.email} />
              <Field label="User ID" value={user.id} mono />
              <Field label="Sign-in" value={user.authProvider} capitalize />
              <Field label="Email verified" value={user.emailVerified ? 'Yes' : 'No'} />
              <Field label="Two-factor" value={user.mfaEnabled ? 'Enabled' : 'Disabled'} />
              <Field label="Joined" value={formatDate(user.createdAt)} />
              <Field
                label="Last login"
                value={user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
              />
              {user.lockedUntil && <Field label="Locked until" value={formatDate(user.lockedUntil)} />}
              {user.deletionScheduledAt && (
                <Field label="Deletion scheduled" value={formatDate(user.deletionScheduledAt)} />
              )}
            </dl>
          </section>

          <section>
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Workspaces
            </h3>
            {user.workspaces.length === 0 ? (
              <p className="mt-2 text-[13px] text-muted-foreground">No active memberships.</p>
            ) : (
              <div className="mt-2 space-y-2">
                {user.workspaces.map((w) => (
                  <div key={w.id} className="rounded-[10px] border border-border p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-foreground">{w.name}</span>
                      <span className="text-[11.5px] text-muted-foreground">
                        {w.tier} · {w.subscriptionStatus} · {w.role.replace('workspace-', '')}
                      </span>
                    </div>
                    {w.organizations.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {w.organizations.map((o) => (
                          <li key={o.id} className="flex justify-between text-[12.5px]">
                            <span className="text-foreground">{o.name}</span>
                            <span className="text-muted-foreground">
                              {o.sourceCount} {o.sourceCount === 1 ? 'source' : 'sources'} ·{' '}
                              {o.timezone}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Active sessions
            </h3>
            {user.sessions.length === 0 ? (
              <p className="mt-2 text-[13px] text-muted-foreground">No active sessions.</p>
            ) : (
              <div className="mt-2 space-y-1.5">
                {user.sessions.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 rounded-[10px] border border-border px-3 py-2 text-[12.5px]"
                  >
                    <span className="min-w-0">
                      <span className="text-foreground">{s.deviceLabel ?? 'Unknown device'}</span>
                      {s.impersonatedByEmail && (
                        <span className="ml-1.5 rounded-full bg-warn/15 px-1.5 py-0.5 text-[10px] font-semibold text-warn">
                          support · {s.impersonatedByEmail}
                        </span>
                      )}
                      <div className="text-[11px] text-muted-foreground">{s.ipAddress ?? '—'}</div>
                    </span>
                    <span className="whitespace-nowrap text-[11px] text-muted-foreground">
                      active {formatDate(s.lastActiveAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <button
            onClick={() => onImpersonate(user)}
            disabled={!canImpersonate}
            className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <LogIn className="h-4 w-4" />
            {user.platformRole
              ? 'Cannot impersonate a platform admin'
              : user.status !== 'active'
                ? `Cannot impersonate a ${user.status} account`
                : 'Log in as this user'}
          </button>
        </div>
      )}
    </Drawer>
  );
};

const Field: React.FC<{ label: string; value: string; mono?: boolean; capitalize?: boolean }> = ({
  label,
  value,
  mono,
  capitalize,
}) => (
  <div className="min-w-0">
    <dt className="text-[11px] text-muted-foreground">{label}</dt>
    <dd
      className={clsx(
        'truncate text-foreground',
        mono && 'font-mono text-[11.5px]',
        capitalize && 'capitalize',
      )}
    >
      {value}
    </dd>
  </div>
);

export default AdminUserDrawer;
